import { sendErrorMetric } from "@expert/cloudwatch-metrics";
import {
    type EligibleProduct,
    useIntegratedFlowStore,
    useProductOfferStore,
    useVerifiedCustomerStore,
} from "@expert/sales-tools";
import { Box, Stack, Text } from "@mantine/core";
import React, { type JSX, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { useAnalytics } from "@/analytics";
import { FullOfferChecklist } from "@/sales-tools/home-product/components/FullOfferChecklist/FullOfferChecklist";
import { InlineSaleTrackerStep } from "@/sales-tools/home-product/components/InlineSaleTracker/InlineSaleTrackerStep";
import { SalesFooter } from "@/sales-tools/home-product/components/StepSpecific/SalesFooter";
import { useLanguageStore } from "@/sales-tools/language-selector/useLanguageStore";
import { useLogger } from "@/sdk";
import { useSessionId } from "@/sdk/sessions/hooks/useSessionId";

import type { IntegratedSalesSteps } from "./types";

import { CollectCustomerInfo } from "./CollectCustomerInfo/CollectCustomerInfo";
import { FullChecklistErrorFallback } from "./FullOfferChecklist/components/OfferChecklistFallbacks";
import { SendLink } from "./SendLink/SendLink";
import { FullOfferChecklistStep } from "./StepSpecific/FullOfferChecklistStep";
import { WaitForEnrollment } from "./StepSpecific/WaitForEnrollment";

export function IntegratedFlow({ product }: { product: EligibleProduct }): JSX.Element {
    // Logger with session context (sessionId, partner, callSid) automatically included
    const logger = useLogger({ module: "IntegratedFlow", supportTeam: "sales" });

    const sessionId = useSessionId();
    const { customer } = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId));
    const [contactMDN, setContactMDN] = useState(customer?.mdn ?? "");
    const [contactEmail, setContactEmail] = useState(customer?.email ?? "");
    const languageState = useLanguageStore();

    const step: IntegratedSalesSteps = useIntegratedFlowStore((state) => state.getCurrentStep(sessionId));
    const { dispatcher } = useAnalytics();

    const primaryProductInfo = useProductOfferStore
        .getState()
        .getProductInfo(sessionId, product.productSku, languageState.language);

    useEffect(() => {
        void dispatcher.dispatchBusinessEvent(`${step}_IntegratedFlow`);
        // oxlint-disable-next-line exhaustive-deps
    }, [step]);

    if (!primaryProductInfo) {
        // sessionId, partner, callSid automatically included
        logger.warn({ product, primaryProductInfo }, "Unable to find primary product info");
        return <Text>An error has occurred while trying to find plan details</Text>;
    }

    const renderStepContent = (salesStep: IntegratedSalesSteps) => {
        switch (salesStep) {
            case "SuggestedOfferStep":
                return (
                    <ErrorBoundary
                        onError={(error) => {
                            logger.error({ error }, "Error rendering full offer checklist");
                            void sendErrorMetric("FullOfferChecklistError", { eventSource: "Offer-Checklist" });
                        }}
                        fallbackRender={() => <FullChecklistErrorFallback />}
                    >
                        <FullOfferChecklistStep
                            product={product}
                            salesChecklist={primaryProductInfo?.salesOfferChecklist}
                            quickRebuttal={primaryProductInfo?.quickRebuttal}
                        />
                    </ErrorBoundary>
                );
            case "CollectCustomerInfoStep":
                return <CollectCustomerInfo />;
            case "WaitingForEnrollmentStep":
                return <WaitForEnrollment product={product} />;
            case "SendLinkStep":
                return (
                    <SendLink
                        product={product}
                        mdn={contactMDN}
                        email={contactEmail}
                        updateMDN={setContactMDN}
                        updateEmail={setContactEmail}
                        sendLinkCommTypeOverride={primaryProductInfo?.sendLinkCommType}
                        helpButtonEnabled={!!primaryProductInfo?.additionalHelpSteps}
                    />
                );
            case "HelpCustomerFindSubscribeButtonStep":
                return (
                    <Stack>
                        <Box flex={1}>
                            {primaryProductInfo?.additionalHelpSteps ? (
                                <FullOfferChecklist salesChecklist={primaryProductInfo.additionalHelpSteps} />
                            ) : (
                                <Text>Unable to find locate steps. Please file a support ticket</Text>
                            )}
                        </Box>
                        <SalesFooter
                            onBack={() =>
                                useIntegratedFlowStore.getState().updateIntegratedStep(sessionId, "SendLinkStep")
                            }
                            continueButtonProps={{ nextStep: "InlineSaleTrackerStep" }}
                            product={product}
                        />
                    </Stack>
                );
            case "InlineSaleTrackerStep":
                return (
                    <InlineSaleTrackerStep
                        productInfo={primaryProductInfo}
                        product={product}
                        productInfoLink={primaryProductInfo?.productInfoLink}
                    />
                );
            default:
                return <></>;
        }
    };

    // TODO: Fix and make sure this works for all static products
    // If no salesOfferChecklist and we're on the SuggestedOfferStep, skip to CollectCustomerInfoStep
    // const effectiveStep =
    //     !primaryProductInfo.salesOfferChecklist && step === "SuggestedOfferStep" ? "CollectCustomerInfoStep" : step;

    return renderStepContent(primaryProductInfo.salesOfferChecklist ? step : "InlineSaleTrackerStep");
}
